import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import Modal from "../components/Common/Modal";
import {
  getImagingReports,
  createImagingReport,
  updateImagingReport,
  deleteImagingReport,
  ImagingType,
  viewImagingReport,
} from "../services/imagingService";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../constants/roles";
import { toast } from "react-toastify";
import CustomSelect from "../components/Common/CustomSelect";

interface ImagingReportsProps {
  patientId: string;
  formatFileSize: (size: number) => string;
  onDataChanged: () => void;
}

const ImagingReports: React.FC<ImagingReportsProps> = ({
  patientId,
  formatFileSize,
  onDataChanged,
}) => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { user } = useAuth();

  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  /* -------- FORM STATE -------- */
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ImagingType>("CT");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await getImagingReports(patientId);
      setReports(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load imaging reports");
    } finally {
      setLoading(false);
    }
  };
  /* -------- FETCH -------- */
  useEffect(() => {
    if (!patientId) return;
    fetchReports();
  }, [patientId]);
  

  /* -------- CREATE -------- */
  const handleAdd = async () => {
    try {
      if (!title || !type || !file) {
        toast.error("Please fill all required fields");
        return;
      }

      setSaving(true);

      const formData = new FormData();
      formData.append("patientId", patientId);
      formData.append("title", title);
      formData.append("type", type);
      formData.append("description", description);

      if (user?.role !== ROLES.SUPERADMIN && user?.id) {
        formData.append("doctorId", String(user.id));
      }

      formData.append("file", file);
      formData.append("file_size", String(file.size));

      await createImagingReport(formData);
      await fetchReports();  
      resetForm();

      onDataChanged();
      setMode(null);
      resetForm();
    } catch (e) {
      console.error("Create imaging failed:", e);
    } finally {
      setSaving(false);
    }
  };
  
  /* -------- UPDATE -------- */
  const handleUpdate = async () => {
    if (!selectedReport) return;
  
    try {
      if (!title || !type) {
        toast.error("Title and type are required");
        return;
      }
  
      setSaving(true);
  
      const formData = new FormData();
      formData.append("title", title);
      formData.append("type", type);
      formData.append("description", description || "");
  
      if (file) {
        formData.append("file", file);
        formData.append("file_size", String(file.size));
      }
  
      await updateImagingReport(selectedReport.id, formData);
      await fetchReports();   //always refetch
      setMode(null);
      resetForm();

      onDataChanged();
      setMode(null);
      resetForm();
    } catch (e) {
      console.error("Update imaging failed:", e);
    } finally {
      setSaving(false);
    }
  };
  
  /* -------- DELETE -------- */
  const handleDelete = async () => {
    if (!selectedReport) return;

    try {
      await deleteImagingReport(selectedReport.id);
      await fetchReports(); 

      onDataChanged(); 
      setIsDeleteOpen(false);
      setSelectedReport(null);

    } catch (e) {
      console.error("Delete imaging failed:", e);
    } finally {
      setSaving(false);
    }
  };
  
  /* -------- HELPERS -------- */
  const resetForm = () => {
    setTitle("");
    setType("CT");
    setDescription("");
    setFile(null);
    setExistingFileName(null);
    setSelectedReport(null);
  };
  
  const openEdit = (report: any) => {
    setSelectedReport(report);
    setTitle(report.title || "");
    setType(report.type || "CT");
    setDescription(report.description || "");
    setFile(null);
    setExistingFileName(report.file_name);
    setMode("edit");
  };
  

  /* -------- UI -------- */
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Imaging Reports
        </h3>
        <button
          onClick={() => {
            resetForm();
            setMode("add");
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={16} />
          Add Imaging Report
        </button>
      </div>

      {loading && <p className="text-gray-500">Loading...</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map((report) => (
          <div key={report.id} className="card relative p-4">
            <div className="absolute top-4 right-4 flex gap-2">
              <button title="Edit"
                onClick={() => openEdit(report)}
                className="text-gray-400 hover:text-gray-700"
              >
                <Edit size={16} />
              </button>
              <button title="Delete"
                onClick={() => {
                  setSelectedReport(report);
                  setIsDeleteOpen(true);
                }}
                className="text-red-400 hover:text-red-600"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <h4 className="font-semibold text-gray-900 pr-14">
              {report.title}
            </h4>

            <p className="text-xs text-gray-500 mt-1">
              {report.type}
            </p>

            {report.description && (
              <p className="text-sm text-gray-600 mt-3">
                {report.description}
              </p>
            )}

          <div className="flex justify-between items-start text-xs text-gray-500 mt-4 gap-2">
            <span className="flex-1 break-all">
              {report.file_name ||
                report.file_url?.split("/").pop() ||
                "-"}
            </span>

            <span className="whitespace-nowrap">
              {report.file_size ? formatFileSize(report.file_size) : "-"}
            </span>
          </div>


          <button
            onClick={async () => {
              try {
                const url = await viewImagingReport(report.id);
                window.open(url, "_blank", "noopener,noreferrer");
              } catch (err: any) {
                toast.error(err.message || "Failed to open report");
              }
            }}
            className="w-full mt-3 btn-secondary text-sm"
          >
            View Report
          </button>


          </div>
        ))}
      </div>

      {/* ADD / EDIT MODAL */}
      <Modal
        title={mode === "edit" ? "Edit Imaging Report" : "Add Imaging Report"}
        isOpen={mode !== null}
        onClose={() => {
          setMode(null);
          resetForm();
        }}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mode === "edit" ? handleUpdate() : handleAdd();
          }}
        >
          <div>
            <label className="block text-sm font-medium">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-field mt-1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">
              Type <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              value={type}
              onChange={(val) => setType(val as ImagingType)}
              options={[
                { value: "CT", label: "CT" },
                { value: "PET-CT", label: "PET-CT" },
                { value: "MRI", label: "MRI" },
                { value: "X-RAY", label: "X-RAY" },
                { value: "ULTRASOUND", label: "ULTRASOUND" },
              ]}
              className="mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">
              Upload PDF <span className="text-red-500">*</span>
            </label>
            <div className="space-y-1">
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const selected = e.target.files?.[0] ?? null;
                setFile(selected);
                if (selected) {
                  setExistingFileName(selected.name);
                }
              }}
              className="mt-1"
              required={mode !== "edit"} 
            />

            {existingFileName && (
              <p className="text-xs text-gray-600">
                Current file:{" "}
                <span className="font-medium">{existingFileName}</span>
              </p>
            )}
          </div>

          </div>

          <div>
            <label className="block text-sm font-medium">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-field mt-1"
              rows={3}
              required
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setMode(null);
                resetForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : mode === "edit"
                ? "Update Imaging Report"
                : "Add Imaging Report"}
            </button>
          </div>
        </form>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        title="Delete Imaging Report"
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      >
        <p className="mb-4">
          Are you sure you want to delete{" "}
          <strong>{selectedReport?.title}</strong>?
        </p>

        <div className="flex justify-end gap-3">
          <button
            className="btn-secondary"
            onClick={() => setIsDeleteOpen(false)}
          >
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ImagingReports;
